import { KeyValueMap } from 'src/app/models/key-value-map';

export function isFunction(obj): boolean {
  return !!(obj && obj.constructor && obj.call && obj.apply);
}

export function isObject(obj): boolean {
  return (obj && typeof obj === 'object' && !Array.isArray(obj));
}

export function getByPath(obj, path: string, defaultValue?: any): any {
  const result = path.split('.').reduce(
    (acumulator, currentValue) => acumulator ? acumulator[currentValue] : defaultValue, obj);
  if (result == null) {
    return defaultValue;
  }
  return result;
}

export function setByPath(obj, path: string, newValue: any): any {
  let currentNode = obj;
  const pathTokens = path.split('.');
  let i = 0;
  for (; i < pathTokens.length - 1; i++) {
    currentNode = currentNode ? currentNode[pathTokens[i]] : null;
  }

  if (currentNode != null) {
    currentNode[pathTokens[i]] = newValue;
  }
}

/**
 * Filters the list based on dynamic key and value
 *
 * @param list - the list to be filtered
 * @param key - the key used for filtering the list
 * @param value - the value that the key should have to be selected by the filter
 * @returns the number of list elements that successfully pass the filter
 */
export function filterAndCount(list, key, value): number {
  return list
    .filter(l => l[key] === value)
    .length;
}

export function toBoolean(value: boolean | string): boolean {
  if (value == null) {
    return false;
  }

  if (typeof value === 'boolean') {
    return value;
  }
  return value.toLowerCase() === 'true';
}

/**
 * Prepares the offset of an element and returns the top and left properties
 *
 * @param className - the class name to be used when getting the element
 * @returns the offset properties top and left
 */
export function getOffset(className): any {
  const el = document.getElementsByClassName(className).item(0) as HTMLElement;
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX
  };
}

/**
 * Calculates the difference in days between two dates
 *
 * @param firstDate - the first date
 * @param secondDate - the second one
 * @returns the difference in days between those two days
 */
 export function getDiffDays(firstDate: Date, secondDate: Date): number {
  const oneDay = 24 * 60 * 60 * 1000;
  const firstDateMs = new Date(firstDate).getTime();
  const secondDateMs = new Date(secondDate).getTime();

  return Math.round((secondDateMs - firstDateMs) / oneDay);
}

/**
 * Flats an object to a single level map.
 *
 * @example
 * // returns {event: 'createInstance', entity.entityId: 123}
 * flatObject({event: 'createInstance', entity: {entityId: 123}})
 */
export function flatObject(obj: KeyValueMap<string, any>): KeyValueMap<string, any> {
  const result = {};
  const path = [];

  function innerFlatObject(anObject: any): void {
    if (anObject !== Object(anObject)) {
      return result[path.join('.')] = anObject;
    }

    for (const key in anObject) {
      if (anObject.hasOwnProperty(key)) {
        path.push(key);
        innerFlatObject(anObject[key]);
        path.pop();
      }
    }
  }
  innerFlatObject(obj);
  return result;
}

export function isDate(date: any): boolean {
  return date instanceof Date && !isNaN(date.valueOf());
}

/**
 * Converts all paths in an object from string to dates if needed.
 * Mutates the object.
 */
export function convertDates(obj: any, paths: string[]): void {
  // TODO: How to handle arrays?
  paths.forEach(path => {
    const value = getByPath(obj, path);
    if (value && !isDate(value)) {
      setByPath(obj, path, new Date(value));
    }
  });
}

export function toArray<T>(item: T | T[]): T[] {
  if (item == null) {
    return null;
  }
  return Array.isArray(item) ? item : [item];
}
